/**
 * Structural member helpers for the viewer: the layer's data shapes plus the
 * colour scheme, mirroring `mep/raceways` and `mep/ducts`.
 *
 * Colours reuse the existing MEP palette conventions (kind-keyed constants,
 * plain hex, no new brand colours). Steel greys for framing, a warm tone for
 * concrete slabs/footings, and a deliberately DESATURATED outline for the
 * lateral placeholder — it is reserved geometry, not a designed system, and
 * should not read as solid as a placed member.
 */

/** Plan metres → three.js (y-up). Same mapping DuctLayer/ConduitLayer use. */
export { planToThree } from "./ducts";

/** A placed column, as the API returns it. */
export interface LayerColumn {
  id: string;
  grid_ref: string;
  storey_id: string;
  level: number;
  x_m: number;
  y_m: number;
  size_m: number;
  base_z_m: number;
  height_m: number;
  designation: string;
}

/** A placed beam or girder. */
export interface LayerBeam {
  id: string;
  role: "girder" | "infill_beam";
  storey_id: string;
  level: number;
  start_x_m: number;
  start_y_m: number;
  end_x_m: number;
  end_y_m: number;
  z_m: number;
  span_m: number;
  designation: string;
}

/** Reserved lateral geometry at a core. */
export interface LayerLateral {
  id: string;
  core_id: string;
  kind: string;
  min_x_m: number;
  min_y_m: number;
  max_x_m: number;
  max_y_m: number;
  base_z_m: number;
  top_z_m: number;
}

/** Member colours by role/kind. */
export const MEMBER_COLORS: Record<string, number> = {
  // Girders carry the bay; drawn brighter than the infill they support.
  girder: 0x8fa3b8,
  infill_beam: 0x6b7c8c,
  column: 0xb0bcc8,
  // Reserved, not designed — muted on purpose.
  lateral: 0x5a6472,
};

/** Colour for a member role/kind, falling back to the column grey. */
export function memberColor(kind: string): number {
  return MEMBER_COLORS[kind] ?? MEMBER_COLORS.column;
}

/**
 * Flat `[x, y, z, x, y, z]` vertex pair for one beam, in three.js space.
 *
 * A beam is a single segment (start → end) at its top-of-steel elevation, so
 * this is two vertices rather than a polyline — unlike a conduit centerline.
 */
export function beamLineVertices(beam: LayerBeam): number[] {
  const { start_x_m, start_y_m, end_x_m, end_y_m, z_m } = beam;
  if (
    ![start_x_m, start_y_m, end_x_m, end_y_m, z_m].every((v) =>
      Number.isFinite(v),
    )
  ) {
    return [];
  }
  // planToThree maps (x, y, z) plan → (x, z, y) three.
  return [start_x_m, z_m, start_y_m, end_x_m, z_m, end_y_m];
}

/** Total placed beam length (m) — a quick takeoff for the layer's HUD. */
export function totalBeamLength(beams: readonly LayerBeam[]): number {
  return beams.reduce((acc, b) => acc + (Number.isFinite(b.span_m) ? b.span_m : 0), 0);
}
